import React, { forwardRef, useContext, useMemo, SVGAttributes } from 'react';
import classnames from 'classnames';
import * as d3Shape from 'd3-shape';
import { motion } from 'framer-motion';
import { CurveString, PathAnimation, SeriesPointXY } from './Types';
import {
  definedAccessorXY,
  getCurve,
  getOrigin,
  getXStepFromSeries,
  noTransition,
  pointXAccessor,
  pointYAccessor,
  usePathMorph,
} from './utils';
import VizContext from './VizContext';

export interface Props extends SVGAttributes<SVGElement> {
  /**
   * Flag for enabling animations, with different types of animation.
   *
   * 'draw' - Draws the line from start to end using the stroke
   * dasharray technique.
   *
   * 'interpolate' - Morphs between the states by increasing/decreasing
   * the number of points on the source line to match the target line and
   * then transitioning the x/y values, and curve between them.
   *
   * 'slide' - For a series that is representing a sliding window of time
   * and should be animated to indicate so. When using this the X domain
   * passed to Viz should be clipped. When used without a curve, this can
   * be clipped to [0, -1], when used with a curve it should be clipped to
   * [+1, -2].
   *
   * @default 'interpolate'
   */
  animate?: boolean | PathAnimation;
  /**
   * Flag to clip the path to the artboard area.
   */
  clipToArtboard?: boolean;
  /**
   * Interpolation curve to smooth the line between each data point.
   * See d3-shape package for available predefined curves or how to
   * create custom curves.
   */
  curve?: CurveString | d3Shape.CurveFactory;
  /**
   * Sets the defined accessor for the line. Invoked for each point
   * in the line, if the defined accessor returns a true value
   * the point will be taken into consideration when generating the line,
   * if a false value is returned, the point will be skipped and the line
   * split.
   */
  definedAccessor?: (point: SeriesPointXY) => boolean;
  /**
   * Sets the origin x value, from where each point will animate from
   * on the x axis. This can be a single value which will be used for
   * all points, or a function that given the point returns a number.
   */
  originX?: number | ((point: SeriesPointXY) => number);
  /**
   * Sets the origin y value, from where each point will animate from
   * on the y axis. This can be a single value which will be used for
   * all points, or a function that given the point returns a number.
   */
  originY?: number | ((point: SeriesPointXY) => number);
  /**
   * Series of data point objects, with X and Y values that lie within
   * the given domains.
   */
  series: SeriesPointXY[];
}

/**
 * Lines series component, that draws a line between the data points given within
 * the series.
 */
export const VizSVGLine = forwardRef<SVGGElement, Props>((props: Props, ref) => {
  const {
    animate,
    className,
    clipToArtboard,
    curve,
    definedAccessor = definedAccessorXY,
    fill = 'none',
    opacity = 1,
    originX,
    originY,
    series,
    ...rest
  } = props;

  const {
    defArtboardClipMaskId,
    getX,
    getY,
    transition,
  } = useContext(VizContext);

  const d = useMemo(() => d3Shape
    .line<SeriesPointXY>()
    .curve(getCurve(curve))
    .defined(definedAccessor)
    .x(pointXAccessor)
    .y(pointYAccessor)(series.map((point) => ({
      x: getX(point.x),
      y: getY(point.y),
    }))), [curve, definedAccessor, getX, getY, series]);

  const refPath = usePathMorph(d, getXStepFromSeries(getX, series), animate, transition, () => d3Shape
    .line<SeriesPointXY>()
    .curve(getCurve(curve))
    .defined(definedAccessor)
    .x(pointXAccessor)
    .y(pointYAccessor)(series.map((point) => ({
      x: getX(getOrigin(point, point.x, originX)),
      y: getY(getOrigin(point, point.y, originY)),
    }))));

  return (
    <g { ...rest }
        className={ classnames('Bitviz__series-line', className) }
        clipPath={ clipToArtboard || animate === 'draw'
          ? `url(#${defArtboardClipMaskId})`
          : undefined }
        fill={ fill }
        ref={ ref }>
      <motion.path
          animate={ { opacity } }
          initial={ { opacity: 0 } }
          ref={ refPath }
          transition={ animate ? transition : noTransition } />
    </g>
  );
});


export default VizSVGLine;
